import { IModel, IViolation } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { AzureStorageLinkedService } from './azure-storage.linkedservice';
import { LinkedService } from './linkedservice';
import { AzureKeyVault } from 'app/model/linkedservice/key-vault';
import { validateAKVLinkedserviceReference, validateNullLinkedserviceReference } from 'app/shared/utils/linkedservice-validation-util';
import { AzureBlobFSLinkedService } from './azure-blob-fs.linkedservice';
import { format } from 'app/shared/utils/string.util';
import { IValidationContext, azureStorageAuthTypes, Credential } from 'shell-models-lib';
import { GlobalVariables } from 'app/services/globals';
import { IHdinsightClusterAuthObject } from 'app/extensibility/metadatas/control/hdinsight-account-select-metadata';

@Metamodel.model({
    name: 'HDInsight',
    display: [
        format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Hdi_DisplayName),
        format(stringResources.ModelName_LinkedServicesTemplate, stringResources.Hdi_DisplayName)
    ]
})
export class HDInsightLinkedService extends LinkedService {

    public static readonly MODEL: IModel;
    @Metamodel.attribute({
        display: stringResources.ModelName_HDInsightClusterUri
    })
    public clusterUri?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_UserName
    })
    public userName?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_Password,
        constraint: {
            validator: validateAKVLinkedserviceReference
        }
    })
    public password?: string | AzureKeyVault; // TODO: need a secure type to make sure this won't be exposed to customer.

    @Metamodel.attribute({
        reference: true,
        type: AzureStorageLinkedService.MODEL,
        constraint: {
            validator: GlobalVariables.enableAllConnectionParamForExternalActivities && __IS_FABRIC_DPE__ ? undefined : HDInsightLinkedService.validation
        }
    })
    public azureStorageLinkedService?: AzureStorageLinkedService | AzureBlobFSLinkedService;

    @Metamodel.attribute({
        display: stringResources.ModelName_HDInsightIsEspEnabled
    })
    public isEspEnabled?: boolean;

    @Metamodel.attribute({
        display: stringResources.FileSystem_DisplayText
    })
    public fileSystem?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_HDInsightClusterAuthTypes
    })
    public clusterAuthObject?: IHdinsightClusterAuthObject;

    @Metamodel.attribute({
        display: stringResources.ModelName_HDInsightClusterAuthObject
    })
    public clusterAuthType?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_HDInsightClusterUAMICredential,
        reference: true
    })
    public credential?: Credential;

    /* TODO: missing properties:
        hcatalogLinkedServiceName: looks like a property which is recently added.
    */

    constructor(name?: string, description?: string) {
        super(name, description);
    }

    public static async validation(linkedservice: LinkedService, referencedResource: LinkedService, violation?: IViolation, context?: IValidationContext): Promise<IViolation[] | undefined> {
        const validators = await validateNullLinkedserviceReference(linkedservice, referencedResource, violation, context) || [];

        if (GlobalVariables.enableHDIStorageLSAuthValidate) {

            // BYOC + GEN2 = UAMI and MSI support when "enableHDIMSI" flag is enabled
            // BYOC + BLOB = No support for UAMI and MSI
            
            if (linkedservice instanceof HDInsightLinkedService && linkedservice.azureStorageLinkedService) {
                if (GlobalVariables.enableHDIMSI && linkedservice.azureStorageLinkedService instanceof AzureBlobFSLinkedService) {
                    return validators;
                } else {
                    HDInsightLinkedService._checkAuthTypeForStorageLinkedServiceAndAddValidationError(linkedservice, validators);
                }
            }
        }

        return validators;
    }

    private static _checkAuthTypeForStorageLinkedServiceAndAddValidationError(linkedservice: HDInsightLinkedService, validators: IViolation[]) : void {

        if (linkedservice.azureStorageLinkedService.authenticationMethod === azureStorageAuthTypes.MSIAuthentication || 
            linkedservice.azureStorageLinkedService.authenticationMethod === azureStorageAuthTypes.UserAssignedManagedIdentity) {
            validators.push({
                message: () =>
                    format(
                        stringResources.Error_InvalidHDIAuthStorageService,
                        linkedservice.azureStorageLinkedService.authenticationMethod
                    )
            });
        }
    }
}
